import { create } from 'zustand';
import toast from 'react-hot-toast';
import api from '../lib/api';
import type { User, UserRole, UserRoleAssignment } from '../types';

interface AdminUsersFilters {
  role: UserRole | 'all';
  isActive: 'true' | 'false' | 'all';
  search: string;
}

interface AdminUsersState {
  users: User[];
  // Active sales reps, loaded independently of the list filters (Works-for options).
  salesReps: User[];
  salesRepsError: boolean;
  loading: boolean;
  error: string | null;
  filters: AdminUsersFilters;

  fetchUsers: () => Promise<void>;
  fetchSalesReps: () => Promise<void>;
  setFilter: (key: keyof AdminUsersFilters, value: string) => void;
  createUser: (data: {
    email: string;
    displayName?: string;
    roles: UserRole[];
    worksForUserId?: string;
  }) => Promise<User>;
  updateUser: (
    id: string,
    updates: {
      displayName?: string;
      email?: string;
      isActive?: boolean;
      sweepExempt?: boolean;
      worksForUserId?: string | null;
    },
  ) => Promise<User>;
  addRole: (id: string, role: UserRole, notes?: string, expiresAt?: string) => Promise<void>;
  makeAssistant: (id: string, worksForUserId: string) => Promise<void>;
  removeRole: (id: string, role: UserRole) => Promise<void>;
  fetchRoleHistory: (id: string) => Promise<UserRoleAssignment[]>;
}

export const useAdminUsersStore = create<AdminUsersState>((set, get) => ({
  users: [],
  salesReps: [],
  salesRepsError: false,
  loading: false,
  error: null,
  filters: { role: 'all', isActive: 'all', search: '' },

  fetchUsers: async () => {
    set({ loading: true, error: null });
    try {
      const { role, isActive, search } = get().filters;
      const params = new URLSearchParams();
      if (role !== 'all') params.set('role', role);
      params.set('isActive', isActive);
      if (search) params.set('search', search);
      const { data } = await api.get<{ users: User[] }>(
        `/api/admin/users?${params.toString()}`,
      );
      set({ users: data.users, loading: false });
    } catch (err: unknown) {
      let message = 'Failed to load users';
      if (typeof err === 'object' && err !== null) {
        const e = err as { response?: { data?: { message?: string } }; message?: string };
        message = e.response?.data?.message ?? e.message ?? message;
      }
      set({ error: message, loading: false });
    }
  },

  fetchSalesReps: async () => {
    try {
      const { data } = await api.get<{ users: User[] }>(
        '/api/admin/users?role=SALES_REP&isActive=true&pageSize=100',
      );
      set({ salesReps: data.users, salesRepsError: false });
    } catch {
      set({ salesReps: [], salesRepsError: true });
      toast.error('Failed to load sales reps');
    }
  },

  setFilter: (key, value) => {
    set({ filters: { ...get().filters, [key]: value } as AdminUsersFilters });
    void get().fetchUsers();
  },

  createUser: async (data) => {
    const res = await api.post<{ user: User }>('/api/admin/users', data);
    await get().fetchUsers();
    return res.data.user;
  },

  updateUser: async (id, updates) => {
    const res = await api.patch<{ user: User }>(`/api/admin/users/${id}`, updates);
    await get().fetchUsers();
    return res.data.user;
  },

  addRole: async (id, role, notes, expiresAt) => {
    await api.post(`/api/admin/users/${id}/roles`, { role, notes, expiresAt });
    await get().fetchUsers();
  },

  makeAssistant: async (id, worksForUserId) => {
    await api.post(`/api/admin/users/${id}/roles`, { role: 'ASSISTANT', worksForUserId });
    await get().fetchUsers();
  },

  removeRole: async (id, role) => {
    await api.delete(`/api/admin/users/${id}/roles/${role}`);
    await get().fetchUsers();
  },

  fetchRoleHistory: async (id) => {
    const { data } = await api.get<{ assignments: UserRoleAssignment[] }>(
      `/api/admin/users/${id}/role-history`,
    );
    return data.assignments;
  },
}));
